import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { mockPartners } from '@/mocks/partners';
import BentoCard from '@/components/BentoCard';
import { SkeletonList } from '@/components/Skeleton';
import { Search, Plus, ArrowUpRight, Activity, Trash2, AlertTriangle, Loader2 } from 'lucide-react';
import type { Partner } from '@/types';

interface DbLead {
  id: string;
  assigned_partner_id: string | null;
  status: string;
  completed_at: string | null;
  created_at: string;
}

interface DbOffer {
  id: string;
  partner_id: string;
  active: boolean;
}

interface DbPayout {
  id: string;
  partner_id: string;
  amount: number;
  currency: string;
}

interface DbDispute {
  id: string;
  payout_id: string | null;
}

interface PartnerWithStats extends Partner {
  pageCount: number;
  leadCount: number;
  salesCount: number;
  revenue: number;
  profit: number;
  disputeRate: number;
  daysSinceConversion: number;
  health: 'green' | 'amber' | 'red';
}

export default function PartnersPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [expandedPartner, setExpandedPartner] = useState<string | null>(null);

  const [partners, setPartners] = useState<Partner[]>(mockPartners);
  const [leads, setLeads] = useState<DbLead[]>([]);
  const [offers, setOffers] = useState<DbOffer[]>([]);
  const [payouts, setPayouts] = useState<DbPayout[]>([]);
  const [disputes, setDisputes] = useState<DbDispute[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isMockFallback, setIsMockFallback] = useState(true);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [partnersRes, leadsRes, offersRes, payoutsRes, disputesRes] = await Promise.all([
        supabase.from('partners').select('*').order('created_at', { ascending: false }),
        supabase.from('leads').select('id, assigned_partner_id, status, completed_at, created_at'),
        supabase.from('offers').select('id, partner_id, active'),
        supabase.from('payouts').select('id, partner_id, amount, currency'),
        supabase.from('disputes').select('id, payout_id'),
      ]);

      let hasPartnerData = false;

      if (partnersRes.error) throw partnersRes.error;
      if (partnersRes.data && partnersRes.data.length > 0) {
        setPartners(partnersRes.data as Partner[]);
        hasPartnerData = true;
      } else {
        setPartners(mockPartners);
      }

      setLeads(leadsRes.data || []);
      setOffers(offersRes.data || []);
      setPayouts(payoutsRes.data || []);
      setDisputes(disputesRes.data || []);
      setIsMockFallback(!hasPartnerData);
    } catch (err: any) {
      setError(err?.message || 'Failed to load partner data');
      setPartners(mockPartners);
      setIsMockFallback(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const togglePartner = (id: string) => {
    setExpandedPartner(prev => prev === id ? null : id);
    setDeleteConfirmId(null);
  };

  const handleDelete = async (partnerId: string) => {
    setDeletingId(partnerId);
    try {
      const { error: delErr } = await supabase.from('partners').delete().eq('id', partnerId);
      if (delErr) throw delErr;
      setPartners(prev => prev.filter(p => p.id !== partnerId));
      setDeleteConfirmId(null);
    } catch (err: any) {
      setError(err?.message || 'Failed to delete partner');
    } finally {
      setDeletingId(null);
    }
  };

  const filtered = useMemo(() => {
    let result = partners;
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(p =>
        p.display_name.toLowerCase().includes(q) ||
        p.legal_name.toLowerCase().includes(q) ||
        p.country.toLowerCase().includes(q)
      );
    }
    return result;
  }, [search, partners]);

  const partnerStats = useMemo((): PartnerWithStats[] => {
    return filtered.map(p => {
      const pLeads = leads.filter(l => l.assigned_partner_id === p.id);
      const pOffers = offers.filter(o => o.partner_id === p.id && o.active);
      const pPayouts = payouts.filter(pay => pay.partner_id === p.id);
      const pPayoutIds = new Set(pPayouts.map(pay => pay.id));
      const pDisputes = disputes.filter(d => d.payout_id && pPayoutIds.has(d.payout_id));
      const disputeRate = pLeads.length > 0 ? Math.round((pDisputes.length / pLeads.length) * 100) : 0;

      const conversions = pLeads.filter(l => l.status === 'completed' || l.status === 'signed' || l.completed_at);
      const salesCount = conversions.length;
      const revenue = pPayouts.reduce((sum, pay) => sum + (pay.amount || 0), 0);
      const profit = revenue; // No cost data available; revenue = profit for partner view

      const lastConversion = conversions.sort((a, b) => {
        const at = a.completed_at || a.created_at;
        const bt = b.completed_at || b.created_at;
        return new Date(bt).getTime() - new Date(at).getTime();
      })[0];
      const daysSinceConversion = lastConversion
        ? Math.floor((Date.now() - new Date(lastConversion.completed_at || lastConversion.created_at).getTime()) / (1000 * 60 * 60 * 24))
        : 999;

      return {
        ...p,
        pageCount: pOffers.length,
        leadCount: pLeads.length,
        salesCount,
        revenue,
        profit,
        disputeRate,
        daysSinceConversion,
        health: daysSinceConversion <= 7 ? 'green' : daysSinceConversion <= 14 ? 'amber' : 'red',
      };
    });
  }, [filtered, leads, offers, payouts, disputes]);

  const tierBadge = (tier: string) => {
    switch (tier) {
      case 'strategic': return { bg: 'var(--accent-dim)', color: 'var(--accent)', border: 'var(--accent-glow)' };
      case 'preferred': return { bg: 'var(--accent-dim)', color: 'var(--accent)', border: 'var(--accent-glow)' };
      default: return { bg: 'var(--bg-input)', color: 'var(--text-tertiary)', border: 'var(--border-primary)' };
    }
  };

  const statusBadge = (status: string) => {
    switch (status) {
      case 'active': return { bg: 'var(--success-bg)', color: 'var(--success-text)', border: 'var(--success-border)' };
      case 'paused': return { bg: 'var(--warning-bg)', color: 'var(--warning-text)', border: 'var(--warning-border)' };
      case 'churned': return { bg: 'var(--danger-bg)', color: 'var(--danger-text)', border: 'var(--danger-border)' };
      default: return { bg: 'var(--bg-surface-raised)', color: 'var(--text-tertiary)', border: 'var(--border-primary)' };
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>Partners</h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
            {filtered.length} partner{filtered.length !== 1 ? 's' : ''}
            {isMockFallback && !loading && (
              <span className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border"
                style={{ color: 'var(--warning-text)', borderColor: 'var(--warning-border)', background: 'var(--warning-bg)' }}
              >Mock</span>
            )}
          </p>
        </div>
        <button
          onClick={() => navigate('/partners/new')}
          className="flex items-center gap-1.5 px-3 py-2 text-sm rounded-xl transition-all whitespace-nowrap"
          style={{ background: 'var(--accent)', color: 'var(--text-inverse)' }}
        >
          <Plus size={16} />
          <span className="hidden sm:inline">Add Partner</span>
        </button>
      </div>

      <div className="relative max-w-md group">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 transition-colors" style={{ color: 'var(--text-muted)' }} />
        <input
          type="text"
          placeholder="Search partners..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full pl-9 pr-4 py-2 text-sm rounded-xl outline-none transition-all"
          style={{ background: 'var(--bg-input)', border: '1px solid var(--border-primary)', color: 'var(--text-primary)' }}
        />
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 rounded-xl border text-sm"
          style={{ borderColor: 'var(--danger-border)', background: 'var(--danger-bg)', color: 'var(--danger-text)' }}
        >
          <AlertTriangle size={16} />
          <span>{error}</span>
          <button onClick={() => { setError(null); fetchAll(); }} className="ml-auto text-xs underline">Retry</button>
        </div>
      )}

      {loading ? (
        <SkeletonList rows={6} />
      ) : (
        <div className="space-y-3">
          {partnerStats.map(p => {
            const isExpanded = expandedPartner === p.id;
            const isConfirming = deleteConfirmId === p.id;

            const detailPanel = (
              <div className="space-y-3 pt-2">
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                  {[
                    { label: 'Pages', value: String(p.pageCount) },
                    { label: 'Leads', value: String(p.leadCount) },
                    { label: 'Sales', value: String(p.salesCount) },
                    { label: 'Revenue', value: `€${p.revenue.toLocaleString()}` },
                    { label: 'Profit', value: `€${p.profit.toLocaleString()}` },
                  ].map(item => (
                    <div key={item.label} className="rounded-lg p-2.5 border" style={{ background: 'var(--bg-input)', borderColor: 'var(--border-separator)' }}>
                      <p className="text-[10px] uppercase tracking-wider mb-1" style={{ color: 'var(--text-muted)' }}>{item.label}</p>
                      <p className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>{item.value}</p>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-2 pt-1 flex-wrap">
                  <button
                    onClick={() => navigate(`/partners/${p.id}`)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium rounded-lg border transition-colors"
                    style={{ color: 'var(--accent)', borderColor: 'var(--accent)' }}
                  >
                    <ArrowUpRight size={12} />
                    View Partner
                  </button>
                  <button className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium rounded-lg border transition-colors"
                    style={{ color: 'var(--text-tertiary)', borderColor: 'var(--border-primary)', background: 'var(--bg-input)' }}
                  >
                    <Activity size={12} />
                    Activity Log
                  </button>
                  {isConfirming ? (
                    <div className="flex items-center gap-2 ml-auto">
                      <span className="text-[11px]" style={{ color: 'var(--danger-text)' }}>Delete this partner?</span>
                      <button
                        onClick={async (e) => { e.stopPropagation(); await handleDelete(p.id); }}
                        disabled={deletingId === p.id}
                        className="flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium rounded-lg border transition-colors disabled:opacity-40"
                        style={{ color: 'var(--danger-text)', borderColor: 'var(--danger-border)', background: 'var(--danger-bg)' }}
                      >
                        {deletingId === p.id ? <Loader2 size={10} className="animate-spin" /> : <Trash2 size={10} />}
                        {deletingId === p.id ? 'Deleting...' : 'Yes, Delete'}
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); setDeleteConfirmId(null); }}
                        className="px-2.5 py-1 text-[11px] rounded-lg transition-colors"
                        style={{ color: 'var(--text-tertiary)' }}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={(e) => { e.stopPropagation(); setDeleteConfirmId(p.id); }}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium rounded-lg border transition-colors ml-auto"
                      style={{ color: 'var(--danger-text)', borderColor: 'var(--danger-border)', background: 'var(--danger-bg)' }}
                    >
                      <Trash2 size={12} />
                      Delete
                    </button>
                  )}
                </div>
              </div>
            );

            return (
              <BentoCard
                key={p.id}
                size="full"
                expandable
                expanded={isExpanded}
                onToggle={() => togglePartner(p.id)}
                active={isExpanded}
                className="row-hover"
                header={
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-2 h-2 rounded-full shrink-0"
                      style={{
                        background: p.health === 'green' ? 'var(--success-dot)' : p.health === 'amber' ? 'var(--warning-dot)' : 'var(--danger-dot)',
                        boxShadow: p.health === 'green' ? '0 0 8px var(--success-glow)' : p.health === 'amber' ? '0 0 8px var(--warning-glow)' : '0 0 8px var(--danger-glow)',
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{p.display_name}</span>
                        <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>{p.legal_name}</span>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border"
                          style={statusBadge(p.status)}
                        >{p.status}</span>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border"
                          style={tierBadge(p.partner_tier)}
                        >{p.partner_tier}</span>
                      </div>
                      <div className="flex items-center gap-3 mt-1 flex-wrap">
                        <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>{p.country}</span>
                        <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>{p.pageCount} pages</span>
                        <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>{p.leadCount} leads</span>
                        <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>{p.salesCount} sales</span>
                      </div>
                    </div>
                  </div>
                }
                footer={detailPanel}
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>{p.country}</span>
                  <span className="text-[10px] font-medium px-2 py-0.5 rounded-full border" style={statusBadge(p.status)}>{p.status}</span>
                  <span className="text-[10px] font-medium px-2 py-0.5 rounded-full border" style={tierBadge(p.partner_tier)}>{p.partner_tier}</span>
                  <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{p.pageCount} pages · {p.leadCount} leads · {p.salesCount} sales</span>
                  <span className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>€{p.revenue.toLocaleString()}</span>
                </div>
              </BentoCard>
            );
          })}
        </div>
      )}
    </div>
  );
}